import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { UpcomingBill } from '../../../hooks/useDashboardData';

interface Props {
  bills: UpcomingBill[];
  loading: boolean;
}

/** Strip installment suffix "(N/M)" → base description */
const baseDesc = (desc: string) => desc.replace(/\s*\(\d+\/\d+\)$/, '').trim();

const isInstallmentDesc = (desc: string) => /\(\d+\/\d+\)$/.test(desc.trim());

interface BillGroup {
  key: string;
  baseDescription: string;
  category: string;
  count: number;
  totalAmount: number;
  earliestDate: string;
  latestDate: string;
  earliestDaysLeft: number;
  ids: string[];
}

/** Group installment series into single rows for dashboard display */
const groupBills = (bills: UpcomingBill[]): BillGroup[] => {
  const map = new Map<string, BillGroup>();

  bills.forEach((b) => {
    const base = baseDesc(b.description);
    const isInstallment = isInstallmentDesc(b.description);
    // Only group if it looks like an installment series; otherwise each is its own group
    const key = isInstallment ? `${base}|${b.category}` : b.id;

    if (!map.has(key)) {
      map.set(key, {
        key,
        baseDescription: base,
        category: b.category,
        count: 0,
        totalAmount: 0,
        earliestDate: b.dueDate,
        latestDate: b.dueDate,
        earliestDaysLeft: b.daysLeft,
        ids: [],
      });
    }

    const g = map.get(key)!;
    g.count += 1;
    g.totalAmount += b.amount;
    g.ids.push(b.id);

    if (b.dueDate < g.earliestDate) {
      g.earliestDate = b.dueDate;
      g.earliestDaysLeft = b.daysLeft;
    }
    if (b.dueDate > g.latestDate) {
      g.latestDate = b.dueDate;
    }
  });

  return Array.from(map.values()).sort((a, b) => a.earliestDate.localeCompare(b.earliestDate));
};

const categoryIcons: Record<string, { icon: string; bg: string; color: string }> = {
  Cartão: { icon: 'ri-bank-card-line', bg: 'bg-violet-100', color: 'text-violet-600' },
  Moradia: { icon: 'ri-home-line', bg: 'bg-amber-100', color: 'text-amber-600' },
  Transporte: { icon: 'ri-car-line', bg: 'bg-sky-100', color: 'text-sky-600' },
  Saúde: { icon: 'ri-heart-pulse-line', bg: 'bg-red-100', color: 'text-red-500' },
  Alimentação: { icon: 'ri-restaurant-line', bg: 'bg-orange-100', color: 'text-orange-500' },
  Outros: { icon: 'ri-bill-line', bg: 'bg-gray-100', color: 'text-gray-500' },
};

const getUrgencyStyle = (daysLeft: number) => {
  if (daysLeft <= 3) return 'bg-red-100 text-red-600';
  if (daysLeft <= 7) return 'bg-amber-100 text-amber-600';
  return 'bg-emerald-100 text-emerald-600';
};

const getUrgencyLabel = (daysLeft: number) => {
  if (daysLeft <= 0) return 'Vencido';
  if (daysLeft === 1) return '1 dia';
  return `${daysLeft} dias`;
};

const formatDate = (dateStr: string) =>
  new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });

const SkeletonRow = () => (
  <div className="flex items-center gap-3 p-3 animate-pulse">
    <div className="w-9 h-9 bg-gray-100 rounded-xl shrink-0" />
    <div className="flex-1 space-y-1.5">
      <div className="h-3 bg-gray-100 rounded w-32" />
      <div className="h-2.5 bg-gray-100 rounded w-20" />
    </div>
    <div className="space-y-1.5 text-right">
      <div className="h-3 bg-gray-100 rounded w-20" />
      <div className="h-2.5 bg-gray-100 rounded w-12" />
    </div>
  </div>
);

const INITIAL_VISIBLE = 8;

export const UpcomingBills = ({ bills, loading }: Props) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const total = bills.reduce((s, b) => s + b.amount, 0);
  const groups = groupBills(bills);
  const visibleGroups = expanded ? groups : groups.slice(0, INITIAL_VISIBLE);
  const hasMore = groups.length > INITIAL_VISIBLE;

  return (
    <div className="bg-white rounded-2xl p-6 border border-gray-100 h-full">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-700 text-gray-900">Contas a Vencer</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Próximos 30 dias
            {!loading && bills.length > 0 && (
              <span className="ml-1 text-gray-400">· {bills.length} {bills.length === 1 ? 'lançamento' : 'lançamentos'}</span>
            )}
          </p>
        </div>
        {!loading && bills.length > 0 && (
          <span className="text-sm font-700 text-amber-600 bg-amber-50 px-3 py-1 rounded-full whitespace-nowrap">
            {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
          </span>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4].map((i) => <SkeletonRow key={i} />)}
        </div>
      ) : groups.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
          <div className="w-14 h-14 flex items-center justify-center bg-emerald-50 rounded-2xl">
            <i className="ri-check-double-line text-2xl text-emerald-400" />
          </div>
          <div>
            <p className="text-sm font-600 text-gray-600">Nenhuma conta a vencer</p>
            <p className="text-xs text-gray-400 mt-0.5">
              Seus lançamentos pendentes aparecem aqui
            </p>
          </div>
          <button
            onClick={() => navigate('/lancamentos')}
            className="text-xs text-emerald-600 font-600 hover:underline cursor-pointer whitespace-nowrap"
          >
            Criar primeiro lançamento →
          </button>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {visibleGroups.map((group) => {
              const catInfo = categoryIcons[group.category] ?? categoryIcons['Outros'];
              const isGrouped = group.count > 1;

              return (
                <div
                  key={group.key}
                  className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer group"
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-xl shrink-0 ${catInfo.bg}`}>
                    <i className={`${catInfo.icon} text-base ${catInfo.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-600 text-gray-900 truncate">
                        {group.baseDescription}
                      </p>
                      {isGrouped && (
                        <span className="text-[10px] font-600 bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-md whitespace-nowrap shrink-0">
                          {group.count}× parcelas
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {isGrouped
                        ? `${formatDate(group.earliestDate)} — ${formatDate(group.latestDate)}`
                        : formatDate(group.earliestDate)}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-700 text-gray-900">
                      {group.totalAmount.toLocaleString('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                      })}
                    </p>
                    <span className={`text-[11px] font-600 px-2 py-0.5 rounded-full ${getUrgencyStyle(group.earliestDaysLeft)}`}>
                      {getUrgencyLabel(group.earliestDaysLeft)}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>

          {hasMore && (
            <button
              onClick={() => setExpanded((p) => !p)}
              className="w-full mt-3 py-2 text-xs font-600 text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-xl transition-colors cursor-pointer"
            >
              {expanded
                ? 'Ver menos'
                : `Ver mais ${groups.length - INITIAL_VISIBLE} ${groups.length - INITIAL_VISIBLE === 1 ? 'item' : 'itens'}`}
            </button>
          )}

          <div className="mt-4 pt-4 border-t border-gray-100">
            <button
              onClick={() => navigate('/lancamentos')}
              className="w-full text-sm text-emerald-600 font-600 hover:text-emerald-700 cursor-pointer transition-colors"
            >
              Ver todas as contas →
            </button>
          </div>
        </>
      )}
    </div>
  );
};
